import {Progress} from '@base-ui/react/progress';

import {CheckmarkIcon} from '@/components/Icons';
import {twx} from '@/utils/tailwindUtils';

export type ProgressBarProps = {
    totalSteps: number;
    finishedSteps: number;
    className?: string;
    'aria-label'?: string;
};

const segmentGradient = (index: number) =>
    index % 2 === 0
        ? 'linear-gradient(90deg, var(--color-malibu), var(--color-heliotrope))'
        : 'linear-gradient(90deg, var(--color-heliotrope), var(--color-malibu))';

const nodeColor = (index: number) => (index % 2 === 0 ? 'var(--color-malibu)' : 'var(--color-heliotrope)');

const Segment = ({index, filled}: {index: number; filled: boolean}) => (
    <div
        className='-mx-0.5 h-2 flex-1'
        style={{background: filled ? segmentGradient(index) : 'var(--color-primary-dark)'}}
    />
);

const NodeCompleted = ({index}: {index: number}) => (
    <div
        className='relative z-10 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full p-[2px]'
        style={{background: nodeColor(index)}}
    >
        <div className='flex h-full w-full items-center justify-center rounded-full bg-romance'>
            <CheckmarkIcon />
        </div>
    </div>
);

const NodeCurrent = ({index, hasNext}: {index: number; hasNext?: boolean}) => (
    <div
        className='relative z-10 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full p-[2px]'
        style={{
            background: hasNext
                ? `linear-gradient(90deg, ${nodeColor(index)}, var(--color-primary-dark))`
                : nodeColor(index),
        }}
    >
        <div className='h-full w-full rounded-full bg-romance' />
    </div>
);

const NodeFuture = () => (
    <div className='relative z-10 h-[18px] w-[18px] shrink-0 rounded-full border-2 border-primary-dark' />
);

const PillProgressBar = ({finishedSteps, totalSteps, className, 'aria-label': ariaLabel}: ProgressBarProps) => (
    <Progress.Root
        aria-label={ariaLabel}
        value={finishedSteps}
        min={0}
        max={totalSteps}
        className={twx('relative h-6 w-full overflow-hidden rounded-full bg-white/10', className)}
    >
        <Progress.Track className='h-full w-full'>
            <Progress.Indicator
                className='absolute inset-y-0 left-0 h-full rounded-full'
                style={{
                    width: `${Math.min(Math.max(finishedSteps / Math.max(totalSteps, 1), 0), 1) * 100}%`,
                    background: segmentGradient(0),
                }}
            />
        </Progress.Track>
    </Progress.Root>
);

const StepProgressBar = ({finishedSteps, totalSteps, className, 'aria-label': ariaLabel}: ProgressBarProps) => {
    const safeFinished = Math.min(Math.max(finishedSteps, 0), totalSteps);

    const nodes = Array.from({length: totalSteps}, (_, i) => {
        const isCompleted = i < safeFinished;
        const isCurrent = i === safeFinished;

        return (
            <div key={`node-${i}`} className='contents'>
                {i > 0 && <Segment index={i - 1} filled={i - 1 < safeFinished} />}
                {isCompleted ? (
                    <NodeCompleted index={i} />
                ) : isCurrent ? (
                    <NodeCurrent index={i} hasNext={i < totalSteps - 1} />
                ) : (
                    <NodeFuture />
                )}
            </div>
        );
    });

    return (
        <Progress.Root
            aria-label={ariaLabel}
            value={safeFinished}
            min={0}
            max={totalSteps}
            className={twx('flex items-center', className)}
        >
            {nodes}
        </Progress.Root>
    );
};

/**
 * Generic progress bar with two variants:
 * - `totalSteps <= 1`: continuous gradient pill
 * - `totalSteps > 1`: step-based nodes connected by gradient segments
 */
const ProgressBar = (props: ProgressBarProps) =>
    props.totalSteps <= 1 ? <PillProgressBar {...props} /> : <StepProgressBar {...props} />;

export default ProgressBar;
